import { useEffect, useMemo, useState } from "react";
import WithAuth from "../../hoc/WithAuth";
import { useContractList } from "../../api/contract";
import { Input } from "../../components/ui/input";
import { AppButton } from "../../components/ui/AppButton";
import { DataTable } from "../../components/ui/data-table";
import { Icons } from "../../assets/images/icons";
import { Contract } from "../../types";
import { Button } from "../../components/ui/button";
import {
  formatDateCreatedValue,
  formatDateToISO,
} from "../../util/date-formatter";
import { getStatusColor } from "../../util/status-formatter";
import { useNavigate, useParams } from "react-router-dom";
import { useTablePrint } from "../../hooks/useTablePrint";
import { ExportButton } from "../../components/ui/ExportButton";
import { useAuthStore } from "../../store/useAuthStore";

const ContractList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const currentUser = useAuthStore.getState().currentUser;

  /** API */
  const { data: contracts, isLoading } = useContractList(id || "", {
    search: searchText || undefined,
    page: currentPage - 1,
    size: pageSize,
  });
  const { handlePrint } = useTablePrint();

  /** Transform API data */
  const transformedContracts = useMemo((): Contract[] => {
    if (!contracts?.content) return [];

    return contracts.content;
  }, [contracts]);

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    console.log(page, size);

    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleRowClick = (contract: any) => {
    if (
      currentUser?.roleName === "ROLE_MFI_ADMIN" ||
      currentUser?.roleName === "ROLE_MFI_OPERATION"
    )
      navigate(`/contracts/${contract.id}`);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [id]);

  /** Define columns for the DataTable */
  const contractColumns = [
    {
      key: "index",
      title: "#",
      dataIndex: "index",
      align: "center" as const,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      key: "contractNumber",
      title: "Contract Number",
      dataIndex: "contractNumber",
      align: "left" as const,
    },
    {
      key: "fullName",
      title: "Full Name",
      dataIndex: "fullName",
      align: "left" as const,
      render: (text: string) => (
        <div className="truncate max-w-[200px]" title={text}>
          {text}
        </div>
      ),
    },
    {
      key: "phoneNumber",
      title: "Phone Number",
      dataIndex: "phoneNumber",
      align: "left" as const,
    },
    {
      key: "harvestDate",
      title: "Harvest Date",
      dataIndex: "harvestDate",
      align: "left" as const,
      render: (value: string) => formatDateToISO(value),
    },
    {
      key: "dateCreated",
      title: "Date Created",
      dataIndex: "dateCreated",
      align: "left" as const,
      render: (_: unknown, record: Contract) =>
        formatDateCreatedValue(record.dateCreated),
    },
    {
      key: "contractStatus",
      title: "Contract Status",
      dataIndex: "contractStatus",
      align: "left" as const,
      render: (value: string) => (
        <div
          className="px-3 py-1 max-w-[85px] rounded-md text-center text-xs font-medium capitalize"
          style={{
            color: getStatusColor(value, "text"),
            backgroundColor: getStatusColor(value, "bg"),
          }}
        >
          {value?.charAt(0).toUpperCase() + value?.slice(1).toLowerCase()}
        </div>
      ),
    },

    (currentUser?.roleName === "ROLE_KFT_SUPER_ADMIN" ||
    currentUser?.roleName === "ROLE_KFT_SP_ADMIN"
      ? [
          {
            key: "actions",
            title: "Actions",
            align: "center" as const,
            render: (_: any, record: Contract) => (
              <Button
                variant="destructive"
                size="sm"
                onClick={(e) => e.stopPropagation()}
                className="h-8 p-0 w-full gap-2 text-[#C41919]"
              >
                <img src={Icons.deleteIcon} alt="delete" className="h-4 w-4" />
                Remove
              </Button>
            ),
          },
        ]
      : []) as any,
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between">
        {/* Search - Desktop */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search contracts by name"
            className="pl-10 rounded-2xl w-[308px] h-11"
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <img src={Icons.search} alt="search" className="w-6 h-6" />
          </div>
        </div>

        <div className="flex gap-2">
          <AppButton
            customIcon={Icons.printIcon}
            variant="outline"
            onClick={() =>
              handlePrint(
                contractColumns,
                transformedContracts,
                "Contract Report",
              )
            }
            className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          >
            Print
          </AppButton>

          <ExportButton
            columns={contractColumns}
            data={transformedContracts}
            fileName="contracts"
            title="Contract Report"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: contracts?.totalElements ?? 0,
            }}
            customIcon={Icons.fileExport}
            className="bg-[#DDDDDD] hover:bg-gray-300"
            menuClassName="w-40"
          />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <DataTable<Contract>
          data={transformedContracts}
          columns={contractColumns}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: contracts?.totalElements ?? 0,
            onChange: handlePaginationChange,
          }}
          onRowClick={
            currentUser?.roleName === "ROLE_MFI_ADMIN" ||
            currentUser?.roleName === "ROLE_MFI_OPERATION"
              ? handleRowClick
              : undefined
          }
          emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
          emptyText="No contracts found"
          rowKey="id"
        />
      </div>
    </>
  );
};

export default WithAuth(ContractList);
